import React from 'react';

/**
 * BlindSpotsBanner — generic risk-flag banner for top picks.
 *
 * Props:
 *   riskFlags: { [picKey]: { flags, labels, severity, count_2026, overdue_ratio, year_diff, trans_pct, count } }
 *   topPicks: array of { id, tier } e.g. [{ id: '2-1-0-1-1', tier: 'ELITE' }]
 *   itemLabel: optional friendly label (e.g. "patterns", "ranges", "diffs")
 *   renderItem: (id) => ReactNode (custom display formatter for the id)
 */
export default function BlindSpotsBanner({ riskFlags, topPicks, itemLabel = 'picks', renderItem }) {
  if (!riskFlags || Object.keys(riskFlags).length === 0) return null;
  if (!topPicks || topPicks.length === 0) return null;

  const flagged = topPicks.filter(p => riskFlags[String(p.id)]);
  if (flagged.length === 0) return null;

  const renderId = renderItem || ((id) => <span className="font-mono font-bold">{id}</span>);

  return (
    <div
      className="bg-gradient-to-r from-amber-50 via-orange-50 to-red-50 border-2 border-amber-300 rounded-xl p-4 shadow-sm"
      data-testid="blind-spots-banner"
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl leading-none">⚠️</div>
        <div className="flex-1">
          <h3 className="font-black text-amber-900 text-sm mb-1">
            ⚠️ Caution — {flagged.length} top {flagged.length === 1 ? 'prediction has' : `${itemLabel} have`} counter-evidence
          </h3>
          <p className="text-[11px] text-amber-800 mb-3">
            These predictions look strong on paper but recent data points the other way. Review the reasons below before relying on them.
          </p>
          <div className="space-y-2">
            {flagged.map((p) => {
              const info = riskFlags[String(p.id)];
              const tier = p.tier || 'TOP';
              const tierLabel = tier === 'ELITE' ? 'ELITE'
                : tier === 'STRONG' ? 'STRONG'
                : tier === 'TOP' ? 'TOP PICK'
                : tier === 'WINDOW' ? 'WHOLE WINDOW'
                : tier;
              const tierBg = tier === 'ELITE'
                ? 'bg-amber-300 text-amber-950'
                : tier === 'STRONG'
                  ? 'bg-emerald-200 text-emerald-900'
                  : tier === 'TOP'
                    ? 'bg-blue-200 text-blue-900'
                    : 'bg-gray-200 text-gray-700';
              const tierEmoji = tier === 'ELITE' ? '🥇' : tier === 'STRONG' ? '🥈' : tier === 'TOP' ? '🎯' : '🏷️';
              return (
                <div
                  key={p.id}
                  className="bg-white border border-amber-200 rounded p-2.5 flex items-center gap-3 flex-wrap"
                  data-testid={`blind-spot-${p.id}`}
                >
                  <span className={`px-2 py-1 rounded font-mono font-black text-base ${tierBg}`}>
                    {renderId(p.id)}
                  </span>
                  <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${tierBg}`}>
                    {tierEmoji} {tierLabel}
                  </span>
                  <div className="flex flex-wrap gap-1.5 flex-1">
                    {info.labels.map((lbl) => (
                      <span
                        key={lbl}
                        className="inline-flex items-center bg-red-100 text-red-700 border border-red-200 rounded px-1.5 py-0.5 text-[10px] font-bold"
                      >
                        ⚠️ {lbl}
                      </span>
                    ))}
                  </div>
                  <div className="text-[10px] text-gray-500 font-mono whitespace-nowrap">
                    {info.count_2026 != null ? `2026: ${info.count_2026}× ·` : ''}
                    {info.trans_pct != null ? ` trans ${info.trans_pct}% ·` : ''}
                    {info.overdue_ratio != null ? ` ×${info.overdue_ratio} overdue` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Helper: build topPicks array from a convergence object. */
export function buildTopPicks(convergence) {
  if (!convergence) return [];
  const picks = [];
  (convergence.elite || []).forEach(id => picks.push({ id, tier: 'ELITE' }));
  (convergence.strong || []).forEach(id => picks.push({ id, tier: 'STRONG' }));
  return picks;
}

/** Helper: small inline ⚠️ chip for individual ELITE/STRONG cards. */
export function RiskChip({ riskFlags, id }) {
  if (!riskFlags) return null;
  const info = riskFlags[String(id)];
  if (!info || !info.flags?.length) return null;
  const sev = info.severity === 'high'
    ? 'bg-red-100 text-red-700 border-red-300'
    : 'bg-amber-100 text-amber-700 border-amber-300';
  const tooltip = info.labels.join(' · ');
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 ml-1 rounded text-[9px] font-bold border ${sev}`}
      title={tooltip}
      data-testid={`risk-${id}`}
    >
      ⚠️ {info.flags.length}
    </span>
  );
}
